import React, { useState } from "react";
import { Edit, Layers } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, FormLabel, Switch, Typography } from "@mui/material";

import { Progress } from "ui";

import { useGetAccountModulesQuery } from "fond/api";
import { useCurrentProject } from "fond/hooks/useCurrentProject";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import OverlaysSettingModal from "fond/map/Overlays/OverlaysSettingModal";
import { dataPrepStageConfig } from "fond/project/panels/dataPrepStages";
import { WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { accountModuleCheck, Actions } from "fond/utils/permissions";
import { StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

const stageConfig: Record<string, { label: string }> = {
  ...dataPrepStageConfig,
  // TODO: Remove these legacy `mu_data_prep` stages (FND-4902) once no executions created before the stage rename remain.
  base_data: { label: "Processing base data" },
  demand: { label: "Generating demand" },
  ug_gen: { label: "Generating underground network" },
  output: { label: "Preparing output" },
};

interface DataPrepPanelProps {
  isReadOnly?: boolean;
  canRun: boolean;
}

const DataPrepPanel: React.FC<DataPrepPanelProps> = ({ isReadOnly, canRun }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const project = useCurrentProject();
  const { open } = useStackedNavigationContext();
  const { workflowState, stages, runWorkflow, cancelWorkflow } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.MU_POWER.DATA_PREP,
  });
  const [serviceRoads, setServiceRoads] = useState<boolean>(false);
  const [showOverlays, setShowOverlays] = useState(false);
  const { data: accountModules } = useGetAccountModulesQuery(project?.Account.ID ?? "");
  const hasOverlaysModule = accountModuleCheck(accountModules, Actions.CUSTOM_OVERLAYS);
  const canEditOverlays = usePermissionCheck(Actions.CUSTOM_OVERLAYS, project?.Permission.Level);

  const isInProgress = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const handleRunDataPrep = () => {
    runWorkflow?.({ ServiceRoads: serviceRoads });
  };

  const handleCancelDataPrep = () => {
    cancelWorkflow?.();
  };

  const handleModifyData = () => {
    open("modification-muPowerBaseData");
  };

  const handleOpenOverlays = () => {
    setShowOverlays(true);
  };

  return (
    <>
      {!canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Upload required layers</AlertTitle>
          Please upload all input layers before running data preparation.
        </Alert>
      )}

      {canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Data is ready for preparation.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation failed</AlertTitle>
          There was an error running data preparation. Please try again.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Data preparation complete.
        </Alert>
      )}

      {stages && (isInProgress || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {!isInProgress && (
        <Box display="flex" flexDirection="column">
          <Box width="100%" display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
            <FormLabel htmlFor="serviceRoads">Include Service Roads</FormLabel>
            <Switch
              id="serviceRoads"
              name="serviceRoads"
              checked={serviceRoads}
              onChange={(event) => setServiceRoads(event.target.checked)}
              disabled={isReadOnly || !canRun}
              data-testid="serviceRoads"
            />
          </Box>
          <Typography variant="caption" color="textSecondary">
            Service roads represent parking aisles, driveways, alleys, and similar routes.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            size="small"
            sx={{ my: 1 }}
            disabled={!runWorkflow || !canRun || isReadOnly}
            onClick={handleRunDataPrep}
          >
            {workflowState === "COMPLETE" ? "Re-run data preparation" : "Run data preparation"}
          </Button>
        </Box>
      )}

      {isInProgress && (
        <Button fullWidth variant="contained" size="small" sx={{ my: 1 }} disabled={!cancelWorkflow || isReadOnly} onClick={handleCancelDataPrep}>
          Cancel
        </Button>
      )}

      {hasOverlaysModule && canEditOverlays && (
        <StackedNavigationButton
          icon={<Layers />}
          title="Manage overlays"
          onClick={handleOpenOverlays}
          disabled={isReadOnly}
          iconSize="small"
          variant="outlined"
          sx={{ m: 0, mt: 1 }}
        />
      )}

      {workflowState === "COMPLETE" && (
        <StackedNavigationButton icon={<Edit />} title="Modify data" onClick={handleModifyData} sx={{ m: 0, mt: 1 }} disabled={isReadOnly} />
      )}

      {canEditOverlays && <OverlaysSettingModal open={showOverlays} onClose={() => setShowOverlays(false)} />}
    </>
  );
};

export default DataPrepPanel;
